/**
 * A form whose contents are assembled from theme blocks and submitted to an
 * arbitrary external endpoint (webhook) instead of a Shopify form handler.
 *
 * The fields are separate blocks (`form-input`, `form-textarea`, `form-checkbox`,
 * `form-submit`) that can be nested in any layout block, so this component never
 * assumes a particular structure — it reads whatever named controls it finds
 * inside its `<form>`.
 *
 * Configuration comes from data attributes rendered by `blocks/external-form.liquid`.
 */

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const HONEYPOT_NAME = '_website';

class ExternalForm extends HTMLElement {
  /** @type {HTMLFormElement | null} */
  #form = null;

  /** @type {number} */
  #loadTime = 0;

  /** @type {boolean} */
  #submitting = false;

  connectedCallback() {
    this.#form = this.querySelector('[data-form]');
    if (!this.#form) return;

    if (!this.#loadTime) this.#loadTime = Date.now();

    // Re-adding the same listener reference is a no-op, so reconnecting is safe.
    this.#form.addEventListener('submit', this.#onSubmit);
  }

  disconnectedCallback() {
    this.#form?.removeEventListener('submit', this.#onSubmit);
  }

  /** @returns {{ required: string, email: string, error: string }} */
  get #messages() {
    return {
      required: this.dataset.messageRequired || 'To pole jest wymagane.',
      email: this.dataset.messageEmail || 'Podaj poprawny adres e-mail.',
      error: this.dataset.messageError || 'Coś poszło nie tak. Spróbuj ponownie za chwilę.',
    };
  }

  /**
   * Every named control inside the form, minus the honeypot and disabled fields.
   *
   * @returns {Array<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>}
   */
  get #controls() {
    if (!this.#form) return [];

    return Array.from(this.#form.querySelectorAll('input, textarea, select')).filter(
      (element) => element.name && element.name !== HONEYPOT_NAME && !element.disabled
    );
  }

  #onSubmit = async (event) => {
    event.preventDefault();
    if (this.#submitting) return;

    const honeypot = this.#form?.querySelector(`[name="${HONEYPOT_NAME}"]`);
    if (honeypot instanceof HTMLInputElement && honeypot.value.trim() !== '') return;

    const minFillTime = Number(this.dataset.minFillTime || 0) * 1000;
    const inDesignMode = Boolean(window.Shopify?.designMode);
    if (!inDesignMode && minFillTime > 0 && Date.now() - this.#loadTime < minFillTime) return;

    if (!this.#validate()) return;

    const endpoint = this.dataset.endpoint;
    const status = this.querySelector('[data-status]');

    if (!endpoint) {
      console.warn('[external-form] No endpoint URL set in the block settings.');
      this.#showStatus(this.#messages.error);
      return;
    }

    const submitButton = this.querySelector('[data-submit]');
    const originalLabel = submitButton?.textContent;

    this.#submitting = true;
    status?.setAttribute('hidden', '');

    if (submitButton instanceof HTMLButtonElement) {
      submitButton.disabled = true;
      if (submitButton.dataset.labelSending) submitButton.textContent = submitButton.dataset.labelSending;
    }

    try {
      const response = await fetch(endpoint, this.#requestInit());
      if (!response.ok) throw new Error(`Request failed with status ${response.status}`);

      this.#showSuccess();
    } catch (error) {
      console.error('[external-form]', error);
      this.#showStatus(this.#messages.error);
    } finally {
      this.#submitting = false;

      if (submitButton instanceof HTMLButtonElement) {
        submitButton.disabled = false;
        if (originalLabel != null) submitButton.textContent = originalLabel;
      }
    }
  };

  /** @returns {RequestInit} */
  #requestInit() {
    if (this.dataset.encoding === 'form-data') {
      const formData = new FormData();

      for (const control of this.#controls) {
        if (control instanceof HTMLInputElement && (control.type === 'checkbox' || control.type === 'radio')) {
          if (control.checked) formData.append(control.name, control.value);
          continue;
        }

        formData.append(control.name, control.value);
      }

      // No Content-Type header — the browser sets it along with the multipart boundary.
      return { method: 'POST', body: formData };
    }

    return {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(this.#payload()),
    };
  }

  /**
   * Builds the JSON body. Unchecked checkboxes are sent as `false` rather than
   * omitted, and repeated names collapse into an array.
   *
   * @returns {Record<string, unknown>}
   */
  #payload() {
    /** @type {Record<string, unknown>} */
    const payload = {};

    for (const control of this.#controls) {
      let value = control.value;

      if (control instanceof HTMLInputElement) {
        if (control.type === 'checkbox') {
          value = control.checked ? (control.value === 'true' ? true : control.value) : false;
        } else if (control.type === 'radio') {
          if (!control.checked) continue;
        } else if (control.type === 'number' && control.value !== '') {
          const parsed = Number(control.value);
          if (!Number.isNaN(parsed)) value = parsed;
        }
      }

      if (Object.prototype.hasOwnProperty.call(payload, control.name)) {
        const existing = payload[control.name];
        payload[control.name] = Array.isArray(existing) ? [...existing, value] : [existing, value];
      } else {
        payload[control.name] = value;
      }
    }

    return payload;
  }

  /** @returns {boolean} */
  #validate() {
    this.#clearErrors();

    /** @type {HTMLElement | null} */
    let firstInvalid = null;

    for (const control of this.#controls) {
      const message = this.#errorFor(control);
      if (!message) continue;

      this.#showFieldError(control, message);
      firstInvalid ??= control;
    }

    firstInvalid?.focus();
    return firstInvalid === null;
  }

  /**
   * @param {HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement} control
   * @returns {string | null}
   */
  #errorFor(control) {
    const { required, email } = this.#messages;

    if (control instanceof HTMLInputElement && control.type === 'checkbox') {
      if (control.required && !control.checked) return control.dataset.errorMessage || required;
      return null;
    }

    const value = control.value.trim();

    if (control.required && value === '') return required;
    if (value === '') return null;

    if (control instanceof HTMLInputElement && control.type === 'email' && !EMAIL_PATTERN.test(value)) {
      return email;
    }

    if (!control.checkValidity()) return control.validationMessage;

    return null;
  }

  /**
   * @param {HTMLElement} control
   * @param {string} message
   */
  #showFieldError(control, message) {
    control.setAttribute('aria-invalid', 'true');

    const target = control.closest('.external-form__field')?.querySelector('[data-field-error]');
    if (target) target.textContent = message;
  }

  #clearErrors() {
    for (const element of this.querySelectorAll('[data-field-error]')) {
      element.textContent = '';
    }

    for (const element of this.querySelectorAll('[aria-invalid]')) {
      element.removeAttribute('aria-invalid');
    }

    this.querySelector('[data-status]')?.setAttribute('hidden', '');
  }

  /** @param {string} message */
  #showStatus(message) {
    const status = this.querySelector('[data-status]');
    if (!status) return;

    const text = status.querySelector('[data-status-text]');
    if (text) text.textContent = message;
    status.removeAttribute('hidden');
  }

  #showSuccess() {
    const success = this.querySelector('[data-success]');

    this.#form?.reset();
    this.#form?.setAttribute('hidden', '');
    this.querySelector('[data-status]')?.setAttribute('hidden', '');

    if (success instanceof HTMLElement) {
      success.removeAttribute('hidden');
      success.focus();
    }
  }
}

if (!customElements.get('external-form')) {
  customElements.define('external-form', ExternalForm);
}
